import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Brain, Plus, Trash, Sparkle, WarningCircle, Info, XCircle } from "@phosphor-icons/react";

const CATS = [
  { value: "durata_etapa", label: "Durată etapă producție" },
  { value: "zincare", label: "Timp zincare" },
  { value: "normare", label: "Normare operativă" },
  { value: "personal", label: "Personal necesar" },
  { value: "capacitate", label: "Capacitate" },
  { value: "altul", label: "Altul" },
];
const WLEVEL = {
  error: { icon: XCircle, cls: "border-red-500/30 bg-red-500/[0.06] text-red-300" },
  warning: { icon: WarningCircle, cls: "border-amber-500/30 bg-amber-500/[0.06] text-amber-300" },
  info: { icon: Info, cls: "border-blue-500/30 bg-blue-500/[0.06] text-blue-300" },
};

function VisioTools() {
  const [s, setS] = useState({ auto_estimate: false, buffer_pct: 15 });
  const [est, setEst] = useState(null);
  const fileRef = { current: null };
  const [pname, setPname] = useState("");
  useEffect(() => { api.get("/visio/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const saveS = async () => { try { const { data } = await api.put("/visio/settings", s); setS(data); toast.success("Setări salvate"); } catch (e) { toast.error("Eroare"); } };
  const estimate = async () => { try { const { data } = await api.post("/visio/estimate", {}); setEst(data); } catch (e) { toast.error("Eroare"); } };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("project_name", pname);
    try { const { data } = await api.post("/visio/learn/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Încărcat în folderul „${data.folder}"`); setPname(""); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="mb-6 grid gap-4 md:grid-cols-2" data-testid="visio-tools">
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="mb-3 font-head text-lg font-bold">Estimare automată termen</h3>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={s.auto_estimate} onChange={(e) => setS({ ...s, auto_estimate: e.target.checked })} data-testid="visio-auto-estimate" /> Estimează automat termenul la fiecare proiect nou</label>
        <div className="mt-2 flex items-center gap-2"><span className="text-xs text-muted-foreground">Buffer %</span><Input type="number" className="h-8 w-24" value={s.buffer_pct} onChange={(e) => setS({ ...s, buffer_pct: Number(e.target.value) || 0 })} data-testid="visio-buffer" /><Button size="sm" variant="outline" onClick={saveS} data-testid="visio-settings-save">Salvează</Button><Button size="sm" onClick={estimate} data-testid="visio-estimate-btn">Estimează acum</Button></div>
        {est && <p className="mt-2 text-sm" data-testid="visio-estimate-result">Estimare: <b>{est.estimated_business_days} zile lucrătoare</b> → termen ~ <b>{est.estimated_date}</b> (bază {est.base_days}z + {est.buffer_pct}% buffer)</p>}
      </div>
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="mb-3 font-head text-lg font-bold">Încarcă proiecte pentru învățare</h3>
        <p className="mb-2 text-xs text-muted-foreground">Se creează automat un folder cu denumirea proiectului.</p>
        <Input placeholder="Denumire proiect (opțional)" value={pname} onChange={(e) => setPname(e.target.value)} className="mb-2" data-testid="visio-project-name" />
        <input type="file" onChange={(e) => upload(e.target.files?.[0])} data-testid="visio-upload-input" className="text-sm" />
      </div>
    </div>
  );
}

export default function VisioAI() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("pm", "inginer", "admin");
  const [knowledge, setKnowledge] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ category: "durata_etapa", key: "", value: "", unit: "", notes: "" });
  const [projectId, setProjectId] = useState("");
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadK = () => api.get("/visio/knowledge").then((r) => setKnowledge(r.data)).catch(() => {});
  useEffect(() => { loadK(); api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);

  const addK = async () => {
    try { await api.post("/visio/knowledge", form); toast.success("Cunoștință adăugată"); setForm({ category: form.category, key: "", value: "", unit: "", notes: "" }); loadK(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delK = async (id) => { try { await api.delete(`/visio/knowledge/${id}`); loadK(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const analyze = async () => {
    setBusy(true); setResult(null);
    try { const { data } = await api.post("/visio/advisor", { project_id: projectId || null, question }); setResult(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 fade-up" data-testid="visio-page">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Inteligență operațională</p>
        <h1 className="font-head text-4xl font-extrabold tracking-tighter flex items-center gap-2"><Brain className="text-primary" weight="fill" /> Visio AI</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Introduci datele operaționale (durate etape, zincare, normare, personal), iar asistentul învață și avertizează despre riscuri: termen prea scurt, mașină lipsă, lipsă de personal.</p>
      </div>

      <VisioTools />
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Asistent */}
        <div className="rounded-xl border border-primary/25 bg-primary/[0.04] p-5">
          <div className="mb-3 flex items-center gap-2"><Sparkle size={20} weight="fill" className="text-primary" /><h3 className="font-head text-lg font-bold">Asistent AI</h3></div>
          <div className="grid gap-2">
            <Select value={projectId || "none"} onValueChange={(v) => setProjectId(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="visio-project"><SelectValue placeholder="Proiect (opțional)" /></SelectTrigger>
              <SelectContent className="max-h-72"><SelectItem value="none">— fără proiect (analiză generală) —</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Întrebare (opțional): ex. Avem resurse pentru acest proiect în termen?" data-testid="visio-question" />
            <Button onClick={analyze} disabled={busy} data-testid="visio-analyze-btn"><Sparkle size={16} className="mr-1.5" /> {busy ? "Analizez…" : "Analizează"}</Button>
          </div>
          {result && (
            <div className="mt-4 space-y-2" data-testid="visio-result">
              {result.summary && <p className="rounded-lg border border-border bg-card/60 p-3 text-sm">{result.summary} <span className="text-[10px] uppercase text-muted-foreground">· {result.source === "ai" ? "AI" : "reguli"}</span></p>}
              {(result.warnings || []).map((w, i) => {
                const meta = WLEVEL[w.level] || WLEVEL.info; const Icon = meta.icon;
                return <div key={i} className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${meta.cls}`}><Icon size={16} className="mt-0.5 shrink-0" /><span>{w.message}</span></div>;
              })}
              {(result.warnings || []).length === 0 && <p className="text-sm text-emerald-400">Niciun risc detectat. 🎉</p>}
            </div>
          )}
        </div>

        {/* Bază de cunoștințe */}
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="mb-3 font-head text-lg font-bold">Bază de cunoștințe</h3>
          {canEdit && (
            <div className="mb-4 grid gap-2 rounded-lg border border-border/70 p-3">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="visio-kcat"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Denumire (ex. Zincare rezervor 500mc)" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} data-testid="visio-kkey" />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Valoare" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="visio-kval" />
                  <Input placeholder="UM (zile/h)" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
                </div>
              </div>
              <Button size="sm" onClick={addK} data-testid="visio-kadd-btn"><Plus size={15} className="mr-1" /> Adaugă</Button>
            </div>
          )}
          <div className="max-h-96 space-y-1.5 overflow-y-auto pr-1">
            {knowledge.map((k) => (
              <div key={k.id} className="flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2 text-sm" data-testid={`visio-k-${k.id}`}>
                <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">{CATS.find((c) => c.value === k.category)?.label || k.category}</span>
                <span className="flex-1 font-medium">{k.key}</span>
                <span className="text-muted-foreground">{k.value} {k.unit}</span>
                {canEdit && <button onClick={() => delK(k.id)} className="text-muted-foreground hover:text-red-400"><Trash size={14} /></button>}
              </div>
            ))}
            {knowledge.length === 0 && <p className="text-sm text-muted-foreground">Nicio cunoștință introdusă încă.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
